/**
 * Allys Filter Panel
 * 
 * Enhanced filter panel with Allys theme styling, micro-interactions, and modern aesthetic
 * Features smooth animations, hover effects, and beautiful gradients
 */

'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
  icon?: React.ReactNode;
}

export interface FilterSection {
  id: string;
  label: string;
  type: 'checkbox' | 'radio' | 'range' | 'price-range';
  options?: FilterOption[];
  min?: number;
  max?: number;
  step?: number;
  defaultExpanded?: boolean;
}

export interface AllysFilterPanelProps {
  /**
   * Filter sections configuration
   */
  sections: FilterSection[];
  /**
   * Current filter values
   */
  values: Record<string, string | string[] | number | { min?: number; max?: number }>;
  /**
   * Callback when filters change
   */
  onChange: (values: Record<string, string | string[] | number | { min?: number; max?: number }>) => void;
  /**
   * Labels for translations
   */
  labels?: {
    title?: string;
    clearAll?: string;
    apply?: string;
    showResults?: string;
  };
  /**
   * Results count
   */
  resultsCount?: number;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Display variant (not used in Allys, but kept for compatibility)
   */
  variant?: 'sidebar' | 'dropdown';
}

/**
 * AllysFilterPanel - Modern filter panel with Allys theme styling
 */
export function AllysFilterPanel({
  sections,
  values,
  onChange,
  labels = {
    title: 'Filters',
    clearAll: 'Clear all',
    apply: 'Apply Filters',
    showResults: 'Show {{count}} results',
  },
  resultsCount,
  className = '',
}: AllysFilterPanelProps) {
  // If only one section, expand it by default and don't show accordion
  const isSingleSection = sections.length === 1;
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(sections.filter(s => s.defaultExpanded !== false || isSingleSection).map(s => s.id))
  );
  
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };
  
  const handleCheckboxChange = (sectionId: string, optionValue: string) => {
    const currentValue = values[sectionId];
    const currentArray = Array.isArray(currentValue) ? currentValue : [];
    
    const newArray = currentArray.includes(optionValue)
      ? currentArray.filter(v => v !== optionValue)
      : [...currentArray, optionValue];
    
    onChange({
      ...values,
      [sectionId]: newArray,
    });
  };
  
  const handleRadioChange = (sectionId: string, optionValue: string) => {
    onChange({
      ...values,
      [sectionId]: optionValue,
    });
  };
  
  const handleRangeChange = (sectionId: string, field: 'min' | 'max', value: number) => {
    const currentValue = values[sectionId] as { min?: number; max?: number } || {};
    
    onChange({
      ...values,
      [sectionId]: {
        ...currentValue,
        [field]: value,
      },
    });
  };
  
  const handleClearAll = () => {
    onChange({});
  };
  
  const hasActiveFilters = Object.keys(values).length > 0;
  const activeFilterCount = Object.values(values).filter(v => {
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'string') return v !== '';
    if (typeof v === 'object' && v !== null) {
      const rangeValue = v as { min?: number; max?: number };
      return rangeValue.min !== undefined || rangeValue.max !== undefined;
    }
    return false;
  }).length;
  
  return (
    <motion.div 
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={`bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 ${className}`}
    >
      {/* Header */}
      <div className="relative px-6 py-5 border-b border-gray-100 bg-gradient-to-br from-[#243462]/5 to-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#243462] to-[#121C3B] flex items-center justify-center shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 font-inter">
                {labels.title}
              </h3>
              {activeFilterCount > 0 && (
                <p className="text-xs text-[#BBA339] font-medium">
                  {activeFilterCount} active {activeFilterCount === 1 ? 'filter' : 'filters'}
                </p>
              )}
            </div>
          </div>
          {hasActiveFilters && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleClearAll}
              className="text-sm text-red-600 hover:text-red-700 font-semibold px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors font-inter"
            >
              {labels.clearAll}
            </motion.button>
          )}
        </div>
      </div>
      
      {/* Filter Sections */}
      <div className="max-h-[calc(100vh-300px)] overflow-y-auto custom-scrollbar">
        {sections.map((section, index) => {
          const isExpanded = expandedSections.has(section.id);
          const hasActiveValues = values[section.id] && (
            (Array.isArray(values[section.id]) && (values[section.id] as string[]).length > 0) ||
            (typeof values[section.id] === 'object' && values[section.id] !== null)
          );
          
          return (
            <motion.div 
              key={section.id} 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="border-b border-gray-100 last:border-b-0"
            >
              {/* Section Header - Hide if only one section */}
              {!isSingleSection && (
                <motion.button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between px-6 py-4 hover:bg-gradient-to-r hover:from-[#243462]/5 hover:to-transparent transition-all duration-300 text-left group"
                  whileHover={{ x: 4 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center gap-3">
                    <span className={`font-bold text-gray-900 transition-colors font-inter ${hasActiveValues ? 'text-[#243462]' : ''}`}>
                      {section.label}
                    </span>
                    {hasActiveValues && (
                      <motion.span 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-2 h-2 rounded-full bg-gradient-to-br from-[#BBA339] to-[#E4CE4A] shadow-sm"
                      />
                    )}
                  </div>
                  <motion.svg
                    className="w-5 h-5 text-gray-600 group-hover:text-[#243462] transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={2.5}
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </motion.svg>
                </motion.button>
              )}
              
              {/* Section Content */}
              {isSingleSection ? (
                // For single section, show content directly without animation
                <div className="space-y-2">
                      {section.type === 'checkbox' && section.options && (
                        <>
                          {section.options.map((option) => {
                            const isChecked = Array.isArray(values[section.id]) && 
                              (values[section.id] as string[]).includes(option.value);
                            
                            return (
                              <motion.label
                                key={option.value}
                                whileHover={{ x: 4 }}
                                className="flex items-center gap-3 cursor-pointer group py-2 px-3 rounded-lg hover:bg-gradient-to-r hover:from-[#243462]/5 hover:to-transparent transition-all duration-200"
                              >
                                <div className="relative">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handleCheckboxChange(section.id, option.value)}
                                    className="peer w-5 h-5 text-[#243462] border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-[#BBA339]/30 focus:border-[#243462] cursor-pointer transition-all hover:border-[#243462]"
                                  />
                                  {isChecked && (
                                    <motion.div
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                                    >
                                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={4}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                      </svg>
                                    </motion.div>
                                  )}
                                </div>
<span className="flex-1 text-sm text-gray-700 group-hover:text-gray-900 font-medium transition-colors font-inter">
                                {option.label}
                                </span>
                                {option.count !== undefined && (
                                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-medium">
                                    {option.count}
                                  </span>
                                )}
                              </motion.label>
                            );
                          })}
                        </>
                      )}
                      
                      {section.type === 'radio' && section.options && (
                        <>
                          {section.options.map((option) => {
                            const isChecked = values[section.id] === option.value;
                            
                            return (
                              <motion.label
                                key={option.value}
                                whileHover={{ x: 4 }}
                                className="flex items-center gap-3 cursor-pointer group py-2 px-3 rounded-lg hover:bg-gradient-to-r hover:from-[#243462]/5 hover:to-transparent transition-all duration-200"
                              >
                                <div className="relative">
                                  <input
                                    type="radio"
                                    checked={isChecked}
                                    onChange={() => handleRadioChange(section.id, option.value)}
                                    className="w-5 h-5 text-[#243462] border-2 border-gray-300 focus:ring-2 focus:ring-[#BBA339]/30 focus:border-[#243462] cursor-pointer transition-all hover:border-[#243462]"
                                  />
                                </div>
                                <span className="flex-1 text-sm text-gray-700 group-hover:text-gray-900 font-medium transition-colors font-inter">
                                  {option.label}
                                </span>
                                {option.count !== undefined && (
                                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-medium">
                                    {option.count}
                                  </span>
                                )}
                              </motion.label>
                            );
                          })}
                        </>
                      )}
                      
                      {(section.type === 'range' || section.type === 'price-range') && (
                        <div className="space-y-4 pt-2">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide font-inter">
                                Min
                              </label>
                              <input
                                type="number"
                                min={section.min}
                                max={section.max}
                                step={section.step || 1}
                                value={(values[section.id] as { min?: number })?.min || section.min || ''}
                                onChange={(e) => handleRangeChange(section.id, 'min', parseFloat(e.target.value))}
                                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-[#BBA339]/30 focus:border-[#243462] transition-all hover:border-gray-300 font-inter"
                                placeholder={section.min?.toString()}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide font-inter">
                                Max
                              </label>
                              <input
                                type="number"
                                min={section.min}
                                max={section.max}
                                step={section.step || 1}
                                value={(values[section.id] as { max?: number })?.max || section.max || ''}
                                onChange={(e) => handleRangeChange(section.id, 'max', parseFloat(e.target.value))}
                                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-[#BBA339]/30 focus:border-[#243462] transition-all hover:border-gray-300 font-inter"
                                placeholder={section.max?.toString()}
                              />
                            </div>
                          </div>
                          
                          {/* Range slider visual */}
                          {section.type === 'price-range' && (
                            <div className="pt-2">
                              <input
                                type="range"
                                min={section.min}
                                max={section.max}
                                step={section.step || 1}
                                value={(values[section.id] as { max?: number })?.max || section.max || 0}
                                onChange={(e) => handleRangeChange(section.id, 'max', parseFloat(e.target.value))}
                                className="w-full h-2 bg-gradient-to-r from-gray-200 to-[#BBA339]/30 rounded-lg appearance-none cursor-pointer slider-thumb"
                                style={{
                                  background: `linear-gradient(to right, #BBA339 0%, #BBA339 ${((((values[section.id] as { max?: number })?.max || section.max || 0) - (section.min || 0)) / ((section.max || 0) - (section.min || 0))) * 100}%, #e5e7eb ${((((values[section.id] as { max?: number })?.max || section.max || 0) - (section.min || 0)) / ((section.max || 0) - (section.min || 0))) * 100}%, #e5e7eb 100%)`
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                </div>
              ) : (
                // For multiple sections, use accordion with animation
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-5 space-y-2">
                        {section.type === 'checkbox' && section.options && (
                          <>
                            {section.options.map((option) => {
                              const isChecked = Array.isArray(values[section.id]) && 
                                (values[section.id] as string[]).includes(option.value);
                              
                              return (
                                <motion.label
                                  key={option.value}
                                  whileHover={{ x: 4 }}
                                  className="flex items-center gap-3 cursor-pointer group py-2 px-3 rounded-lg hover:bg-gradient-to-r hover:from-[#243462]/5 hover:to-transparent transition-all duration-200"
                                >
                                  <div className="relative">
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => handleCheckboxChange(section.id, option.value)}
                                      className="peer w-5 h-5 text-[#243462] border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-[#BBA339]/30 focus:border-[#243462] cursor-pointer transition-all hover:border-[#243462]"
                                    />
                                    {isChecked && (
                                      <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                                      >
                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={4}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                      </motion.div>
                                    )}
                                  </div>
<span className="flex-1 text-sm text-gray-700 group-hover:text-gray-900 font-medium transition-colors font-inter">
                                {option.label}
                                </span>
                                {option.count !== undefined && (
                                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-medium">
                                    {option.count}
                                  </span>
                                )}
                              </motion.label>
                            );
                          })}
                        </>
                      )}
                      
                      {section.type === 'radio' && section.options && (
                          <>
                            {section.options.map((option) => {
                              const isChecked = values[section.id] === option.value;
                              
                              return (
                                <motion.label
                                  key={option.value}
                                  whileHover={{ x: 4 }}
                                  className="flex items-center gap-3 cursor-pointer group py-2 px-3 rounded-lg hover:bg-gradient-to-r hover:from-[#243462]/5 hover:to-transparent transition-all duration-200"
                                >
                                  <div className="relative">
                                    <input
                                      type="radio"
                                      checked={isChecked}
                                      onChange={() => handleRadioChange(section.id, option.value)}
                                      className="w-5 h-5 text-[#243462] border-2 border-gray-300 focus:ring-2 focus:ring-[#BBA339]/30 focus:border-[#243462] cursor-pointer transition-all hover:border-[#243462]"
                                    />
                                  </div>
                                  <span className="flex-1 text-sm text-gray-700 group-hover:text-gray-900 font-medium transition-colors font-inter">
                                    {option.label}
                                  </span>
                                  {option.count !== undefined && (
                                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-medium">
                                      {option.count}
                                    </span>
                                  )}
                                </motion.label>
                              );
                            })}
                          </>
                        )}
                        
                        {(section.type === 'range' || section.type === 'price-range') && (
                          <div className="space-y-4 pt-2">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide font-inter">
                                  Min
                                </label>
                                <input
                                  type="number"
                                  min={section.min}
                                  max={section.max}
                                  step={section.step || 1}
                                  value={(values[section.id] as { min?: number })?.min || section.min || ''}
                                  onChange={(e) => handleRangeChange(section.id, 'min', parseFloat(e.target.value))}
                                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-[#BBA339]/30 focus:border-[#243462] transition-all hover:border-gray-300 font-inter"
                                  placeholder={section.min?.toString()}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide font-inter">
                                  Max
                                </label>
                                <input
                                  type="number"
                                  min={section.min}
                                  max={section.max}
                                  step={section.step || 1}
                                  value={(values[section.id] as { max?: number })?.max || section.max || ''}
                                  onChange={(e) => handleRangeChange(section.id, 'max', parseFloat(e.target.value))}
                                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-[#BBA339]/30 focus:border-[#243462] transition-all hover:border-gray-300 font-inter"
                                  placeholder={section.max?.toString()}
                                />
                              </div>
                            </div>
                            
                            {/* Range slider visual */}
                            {section.type === 'price-range' && (
                              <div className="pt-2">
                                <input
                                  type="range"
                                  min={section.min}
                                  max={section.max}
                                  step={section.step || 1}
                                  value={(values[section.id] as { max?: number })?.max || section.max || 0}
                                  onChange={(e) => handleRangeChange(section.id, 'max', parseFloat(e.target.value))}
                                  className="w-full h-2 bg-gradient-to-r from-gray-200 to-[#BBA339]/30 rounded-lg appearance-none cursor-pointer slider-thumb"
                                  style={{
                                    background: `linear-gradient(to right, #BBA339 0%, #BBA339 ${((((values[section.id] as { max?: number })?.max || section.max || 0) - (section.min || 0)) / ((section.max || 0) - (section.min || 0))) * 100}%, #e5e7eb ${((((values[section.id] as { max?: number })?.max || section.max || 0) - (section.min || 0)) / ((section.max || 0) - (section.min || 0))) * 100}%, #e5e7eb 100%)`
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </motion.div>
          );
        })}
      </div>
      
      {/* Results Summary Footer */}
      {resultsCount !== undefined && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="px-6 py-4 bg-gradient-to-br from-[#243462]/5 to-white border-t border-gray-100"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#BBA339] to-[#E4CE4A] flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-sm">{resultsCount}</span>
              </div>
              <span className="text-sm font-medium text-gray-700 font-inter">
                {resultsCount === 1 ? 'property found' : 'properties found'}
              </span>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Custom scrollbar styles */}
      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #BBA339, #E4CE4A);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #a89333, #d4be45);
        }
        
        /* Custom range slider thumb */
        .slider-thumb::-webkit-slider-thumb {
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: linear-gradient(135deg, #BBA339, #E4CE4A);
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(187, 163, 57, 0.4);
          transition: all 0.2s ease;
        }
        .slider-thumb::-webkit-slider-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 4px 12px rgba(187, 163, 57, 0.6);
        }
        .slider-thumb::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: linear-gradient(135deg, #BBA339, #E4CE4A);
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(187, 163, 57, 0.4);
          border: none;
          transition: all 0.2s ease;
        }
        .slider-thumb::-moz-range-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 4px 12px rgba(187, 163, 57, 0.6);
        }
      `}</style>
    </motion.div>
  );
}

